/**
 * Published prices for Crimp Lifetime: one payment for any size system, the
 * installation Blueprint, exports and the included AI credits, yours for the
 * lifetime of the product. Priced per currency, never FX-converted — $49 and
 * €45 are separate decisions, not one number run through a rate. Licences sold
 * before 26 October 2026 under the name "Crimp Pro (lifetime)" are Crimp
 * Lifetime licences and were sold at exactly these amounts, so an existing
 * holder's receipt still matches this table.
 *
 * These must match the merchant of record's dashboard. `regular` is the full
 * price once the launch window closes, `launch` is what Crimp Lifetime costs
 * today and what every surface displays before Paddle has confirmed a total.
 */
export const CURRENCY_CODES = ["USD", "EUR", "GBP", "SEK", "CAD", "AUD"] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];

/** Public pricing surface. Kept here so the boot router and marketing links
 * share one path instead of growing separate string literals. */
export const PRICING_PATH = "/pricing";

/** `/pricing` (case- and trailing-slash tolerant) is the public pricing page. */
export function isPricingPath(pathname: string): boolean {
  const normalized = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return normalized === PRICING_PATH;
}

/**
 * Cloud projects a free or Crimp Lifetime account may hold, as published
 * (buying Lifetime never adds a limit; Pro lifts it). The live number is
 * `app_runtime_config.cloud_project_limit`, served as
 * `BillingConfig.cloudProjectLimit`: a surface that has the config reads
 * `config.cloudProjectLimit ?? LIFETIME_CLOUD_PROJECT_LIMIT`, and the static
 * pages (the terms, the landing FAQ before the config arrives) print this.
 * Projects kept on the device are never counted.
 */
export const LIFETIME_CLOUD_PROJECT_LIMIT = 5;

/** AI credits that come with Crimp Lifetime, once (`ai_plan_allowances.lifetime`,
 * the `lifetime-v1` bucket). The live number is `BillingConfig.includedCredits.lifetime`,
 * read from that table; this is the seed, printed where no config is loaded.
 * Top-ups add to them; nothing resets them. */
export const LIFETIME_INCLUDED_CREDITS = 50;

export type CurrencyPrice = {
  code: CurrencyCode;
  /** Full price once the launch window closes. */
  regular: number;
  launch: number;
  /** Rendered form, symbol included: "$79", "€69", "A$119". */
  format: (amount: number) => string;
};

const dollar = (prefix: string) => (amount: number) => `${prefix}${amount}`;

export const PRICING: Record<CurrencyCode, CurrencyPrice> = {
  USD: { code: "USD", regular: 79, launch: 49, format: dollar("$") },
  EUR: { code: "EUR", regular: 69, launch: 45, format: (a) => `€${a}` },
  GBP: { code: "GBP", regular: 59, launch: 39, format: (a) => `£${a}` },
  SEK: { code: "SEK", regular: 795, launch: 495, format: (a) => `${a} kr` },
  CAD: { code: "CAD", regular: 99, launch: 59, format: dollar("C$") },
  AUD: { code: "AUD", regular: 119, launch: 79, format: dollar("A$") },
};

const EUROZONE = new Set([
  "AT", "BE", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV",
  "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES",
]);

function currencyForRegion(region: string): CurrencyCode | null {
  const r = region.toUpperCase();
  if (r === "GB") return "GBP";
  if (r === "CA") return "CAD";
  if (r === "AU" || r === "NZ") return "AUD";
  if (EUROZONE.has(r)) return "EUR";
  if (r === "US") return "USD";
  if (r === "SE") return "SEK";
  if (["NO", "DK", "CH", "PL", "CZ", "IS", "RO", "BG", "HU"].includes(r)) {
    return "EUR";
  }
  return null;
}

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (CURRENCY_CODES as readonly string[]).includes(value);
}

/** Picks which price tab to show first from the browser's language tags —
 *  never what anyone pays. */
export function detectCurrency(
  tags: readonly string[] = typeof navigator === "undefined"
    ? []
    : [navigator.language, ...(navigator.languages ?? [])],
): CurrencyCode {
  for (const tag of tags) {
    if (!tag) continue;
    const region = tag.split("-")[1];
    if (!region) continue;
    const hit = currencyForRegion(region);
    if (hit) return hit;
  }
  return "USD";
}

/** Dormant with the Crimp Pass: its one payment spread over the Pass's 12
 *  months, for "about $4 a month" copy. Whole units only: it is a comparison
 *  with a subscription, never an amount anybody is billed. Nothing renders it
 *  while the Pass is not sold; it stays so the Pass can come back unchanged. */
export function passMonthlyEquivalent(code: CurrencyCode): string {
  const price = PRICING[code];
  return price.format(Math.round(price.launch / 12));
}

/**
 * The two Pro prices under test on the pricing page. Arm `a` shows Pro at the
 * Lifetime figure a month; arm `b` shows it cheaper. Which one a browser sees
 * is decided once by `proPriceArm()` in `pro-price-arm.ts`; the interest card
 * records the amount shown, so the arm is recoverable from every signal.
 */
export const PRO_PRICE_ARMS = ["a", "b"] as const;
export type ProPriceArm = (typeof PRO_PRICE_ARMS)[number];

/** Arm `b`'s monthly figure per currency — like `PRICING`, a decision per
 *  market, not a conversion. */
const PRO_ARM_B_MONTHLY: Record<CurrencyCode, number> = {
  USD: 29, EUR: 27, GBP: 23, SEK: 295, CAD: 35, AUD: 47,
};

export type ProDisplayPricing = {
  code: CurrencyCode;
  arm: ProPriceArm;
  /** Billed monthly: the Lifetime figure in arm `a`, the lower table in arm `b`. */
  monthly: number;
  /** Billed yearly: ten months, so two come free against monthly. */
  yearly: number;
  /** The yearly amount per month, rounded, for the toggle's headline. */
  yearlyPerMonth: number;
  format: (amount: number) => string;
};

/** Crimp Pro as the pricing page displays it while it is still interest-only.
 *  Nothing is sold at these amounts; the interest card records which of them a
 *  visitor saw when they asked for early access. The arm is explicit so a
 *  caller can never show one price while attributing another. */
export function proDisplayPricing(code: CurrencyCode, arm: ProPriceArm): ProDisplayPricing {
  const price = PRICING[code];
  const monthly = arm === "b" ? PRO_ARM_B_MONTHLY[code] : price.launch;
  const yearly = monthly * 10;
  return { code, arm, monthly, yearly, yearlyPerMonth: Math.round(yearly / 12), format: price.format };
}
